import React, { useState, useEffect } from 'react';
import { repairInvoicesService } from '../../services/repairInvoicesService';
import Button from './Button';
import SearchInput from './SearchInput';

interface RepairOrderSelectionModalProps {
  onSelect: (order: any) => void;
  onClose: () => void;
}

const RepairOrderSelectionModal: React.FC<RepairOrderSelectionModalProps> = ({
  onSelect,
  onClose
}) => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchCompletedOrders = async () => {
      try {
        setLoading(true);
        const { orders: data, error: fetchError } = await repairInvoicesService.getCompletedRepairOrders();

        if (fetchError) {
          throw fetchError;
        }

        setOrders(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch repair orders');
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedOrders();
  }, []);

  const filteredOrders = orders.filter(order =>
    order.repair_order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.customer?.name && order.customer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (order.device_brand && order.device_brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (order.device_model && order.device_model.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelect = (order: any) => {
    onSelect(order);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Select Repair Order
                </h3>

                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                    {error}
                  </div>
                )}

                {/* Search */}
                <div className="mb-4">
                  <SearchInput
                    placeholder="Search repair orders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Orders List */}
                <div className="max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
                    </div>
                  ) : filteredOrders.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {orders.length === 0
                        ? "No completed repair orders available for invoicing."
                        : "No repair orders match your search."
                      }
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredOrders.map((order) => (
                        <div
                          key={order.id}
                          className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleSelect(order)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="text-sm font-medium text-gray-900">
                                  {order.repair_order_number}
                                </h4>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Completed
                                </span>
                              </div>
                              <div className="mt-1 text-sm text-gray-600">
                                <div>Customer: {order.customer?.name || 'Unknown'}</div>
                                <div>Device: {order.device_brand} {order.device_model}</div>
                                <div>Serial: {order.device_serial}</div>
                                <div>Problem: {order.device_problem}</div>
                              </div>
                              <div className="mt-2 text-xs text-gray-500">
                                Completed: {new Date(order.completed_date || order.updated_at).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="ml-4">
                              <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelect(order);
                                }}
                              >
                                Select
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="w-full sm:w-auto sm:ml-3"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairOrderSelectionModal;